import { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "@/mocks/adminData";

type SyncStatus = "checking" | "online" | "offline" | "partial";

export default function SyncStatusBar({ token }: { token: string }) {
  const [status, setStatus] = useState<SyncStatus>("checking");
  const [lastSync, setLastSync] = useState<string>("");
  const [checking, setChecking] = useState(false);

  const checkSync = useCallback(async () => {
    setChecking(true);
    setStatus("checking");
    try {
      const res = await fetch(`${API_BASE_URL}/admin/partners/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.code === 200) {
        setStatus("online");
        const now = new Date();
        setLastSync(`${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}`);
      } else {
        setStatus("offline");
      }
    } catch {
      setStatus("offline");
    }
    setChecking(false);
  }, [token]);

  useEffect(() => { checkSync(); }, [checkSync]);

  const statusConfig = {
    checking: { bg: "bg-gray-50 border-gray-200", dot: "bg-gray-400 animate-pulse", text: "text-gray-500", label: "正在检测数据库连接...", sub: "" },
    online: { bg: "bg-green-50 border-green-200", dot: "bg-green-500", text: "text-green-700", label: "数据库连接正常", sub: `前台显示实时数据库数据${lastSync ? `，最后同步 ${lastSync}` : ""}` },
    offline: { bg: "bg-orange-50 border-orange-200", dot: "bg-orange-400", text: "text-orange-700", label: "数据库连接失败", sub: "前台显示本地缓存数据，后台操作将保存到本地缓存" },
    partial: { bg: "bg-yellow-50 border-yellow-200", dot: "bg-yellow-400", text: "text-yellow-700", label: "部分功能离线", sub: "部分数据来自本地缓存" },
  };

  const cfg = statusConfig[status];

  return (
    <div className={`w-full border-b ${cfg.bg}`}>
      <div className="max-w-3xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`}></div>
          <div className="min-w-0">
            <span className={`text-xs font-semibold ${cfg.text}`}>{cfg.label}</span>
            {cfg.sub && (
              <span className={`text-xs ml-1.5 ${cfg.text} opacity-70 hidden sm:inline`}>{cfg.sub}</span>
            )}
          </div>
        </div>
        <button
          onClick={checkSync}
          disabled={checking}
          className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border cursor-pointer transition-all whitespace-nowrap flex-shrink-0 ${
            status === "online" ? "border-green-300 text-green-600 hover:bg-green-100"
            : status === "checking" ? "border-gray-200 text-gray-400"
            : "border-orange-300 text-orange-600 hover:bg-orange-100"
          }`}
        >
          <i className={`ri-refresh-line text-xs ${checking ? "animate-spin" : ""}`}></i>
          {checking ? "检测中" : "重新检测"}
        </button>
      </div>
      {cfg.sub && (
        <div className="sm:hidden max-w-3xl mx-auto px-4 pb-2">
          <span className={`text-xs ${cfg.text} opacity-70`}>{cfg.sub}</span>
        </div>
      )}
    </div>
  );
}
